// src/App.js
import React, { useState, useEffect } from "react";
import { BrowserRouter as Router, Routes, Route, Navigate } from "react-router-dom";
import { onAuthStateChanged, getIdTokenResult } from "firebase/auth";
import { doc, getDoc } from "firebase/firestore"; // <--- ADDED THESE IMPORTS
import { auth, db } from "./firebaseConfig";      // <--- ADDED 'db' IMPORT
import { isAccountBlockedStatus, statusLabelFromRaw } from "./utils/userStatus";

// Import Components (existing)
import Login from "./components/homepage/Login";
import AdminDashboard from "./components/admin/dashboard/AdminDashboard";
import StudentDashboard from "./components/student/dashboard/StudentDashboard";
import TutorDashboard from "./components/tutor/TutorDashboard";
import SubcategoryResourceManager from "./components/admin/dsat/SubcategoryResourceManager";
import Homepage from "./components/homepage/Homepage";
import Signup from "./components/homepage/Signup";
import VersionChecker from "./components/VersionChecker";
import SessionLogDetailPage from "./components/session_logs/SessionLogDetailPage";
import ProgressReportDetailPage from "./components/session_logs/ProgressReportDetailPage";
import TutorSessionLogModal from "./components/tutor/session_logs/TutorSessionLogModal";
import AnnouncementDetailPage from "./components/tutor/mobile/AnnouncementDetailPage";
import TutorDiagnosticReportPage from "./components/tutor/diagnostics/TutorDiagnosticReportPage";
import StandaloneDiagnosticReport from "./components/admin/diagnostics/StandaloneDiagnosticReport";
import SatPlanWorkspacePage from "./components/admin/diagnostics/SatPlanWorkspacePage";
import CustomPlanWorkspacePage from "./components/admin/student/CustomPlanWorkspacePage";
import { ADMIN_PORTAL_ROUTE_PATHS } from "./components/admin/dashboard/adminPortalRoutes";
import { TUTOR_PORTAL_ROUTE_PATHS } from "./components/tutor/tutorPortalRoutes";

// Import public pages
import TutoringMain from "./pages/TutoringMain";
import SATTutoring from "./pages/SATTutoring";
import APTutoring from "./pages/APTutoring";
import AdmissionsPage from "./pages/AdmissionsPage";
import AboutPage from "./pages/AboutPage";
import ContactPage from "./pages/ContactPage";

// ------------------------------
// ✅ Small helper to set tab title per page
// ------------------------------
function PageTitle({ title, children }) {
  useEffect(() => {
    document.title = title;
  }, [title]);

  return children;
}

// Roles the app knows how to route. Anything else is treated as "no role".
const VALID_ROLES = ["student", "tutor", "admin", "parent"];

/**
 * Normalizes a role value coming from Firestore or an auth token claim.
 * Guards against casing/whitespace differences (e.g. "Tutor" typed by hand in
 * the Firestore console) silently failing every `userRole === "tutor"` check.
 */
function normalizeRole(value) {
  const role = String(value || "").trim().toLowerCase();
  return VALID_ROLES.includes(role) ? role : null;
}

// Parent placeholder (Keep this for now)
function PlaceholderDashboard({ role }) {
  const handleLogout = () => auth.signOut();
  return (
    <div style={{ padding: "20px" }}>
      <h2>{role} Dashboard</h2>
      <p>This dashboard is not yet built.</p>
      <button onClick={handleLogout}>Logout</button>
    </div>
  );
}

function App() {
  const [user, setUser] = useState(null);
  const [userRole, setUserRole] = useState(null);
  const [accountBlocked, setAccountBlocked] = useState(false);
  const [accountStatusLabel, setAccountStatusLabel] = useState("");
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (currentUser) => {
      if (currentUser) {
        setUser(currentUser);
        try {
          // ROLE RESOLUTION — the Firestore users/{uid} document is the SOURCE
          // OF TRUTH, and the auth token claim is only a fallback.
          //
          // Why this order matters: firestore.rules decide admin/tutor access by
          // reading users/{uid}.role, and admins change roles from Account
          // Management, which writes that document. The custom claim is written
          // server-side (at signup, and now by the syncUserRoleClaim trigger),
          // so it can briefly lag a fresh role change — and before that trigger
          // existed it never got updated at all. Trusting the claim first made a
          // student promoted to tutor keep landing on the Student Dashboard even
          // though every rule and every list already treated them as a tutor.
          //
          // The claim is still read (and force-refreshed) so it can cover the
          // case where the users document is missing or has no role.
          const idTokenResult = await getIdTokenResult(currentUser, true);
          const claimRole = normalizeRole(idTokenResult.claims?.role);

          let userDocData = null;
          const userDocRef = doc(db, "users", currentUser.uid);
          const userDocSnap = await getDoc(userDocRef);
          if (userDocSnap.exists()) {
            userDocData = userDocSnap.data() || {};
          }

          const docRole = normalizeRole(userDocData?.role);
          const role = docRole || claimRole;

          if (docRole && claimRole && docRole !== claimRole) {
            // Not an error: the document wins. Logged because it means the
            // account's auth claim is still catching up (or the sync trigger
            // hasn't been deployed), which affects callables that check
            // request.auth.token.role.
            console.warn(
              `Role claim "${claimRole}" is out of date; using Firestore role "${docRole}".`
            );
          }

          const statusRaw = userDocData?.status || "";
          setAccountBlocked(isAccountBlockedStatus(statusRaw));
          setAccountStatusLabel(statusLabelFromRaw(statusRaw));
          setUserRole(role || null);
        } catch (error) {
          console.error("Error getting user role:", error);
          setUserRole(null);
          setAccountBlocked(false);
          setAccountStatusLabel("");
          // Don't sign out immediately on error, let them see the error page
        }
      } else {
        setUser(null);
        setUserRole(null);
        setAccountBlocked(false);
        setAccountStatusLabel("");
      }
      setLoading(false);
    });

    return () => unsubscribe();
  }, []);

  // IMPORTANT: Render the Router even during auth loading.
  // If we return a plain div here (no Router), the URL is preserved in window.location
  // but React Router is not mounted yet. Once auth resolves and Router mounts, it reads
  // window.location — which is still correct — BUT React Router would see no matching
  // route for /announcement?id=XYZ because at the top-level it would catch it with
  // path="*" → Navigate to="/". Wrapping the loading state in the same Router instance
  // keeps React Router mounted throughout so the URL context is never lost.
  if (loading) {
    return (
      <>
        <VersionChecker />
        <Router>
          <Routes>
            <Route
              path="*"
              element={
                <PageTitle title="Loading… | True Education Cumming">
                  <div style={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    height: "100vh",
                    fontSize: 14,
                    color: "#71717a",
                  }}>
                    Loading…
                  </div>
                </PageTitle>
              }
            />
          </Routes>
        </Router>
      </>
    );
  }

  if (user && accountBlocked) {
    return (
      <>
        <VersionChecker />
        <Router>
          <Routes>
            <Route
              path="*"
              element={
                <PageTitle title="Account Disabled | True Education Cumming">
                  <div style={{ padding: 24, maxWidth: 720 }}>
                    <h3>Account Access Disabled</h3>
                    <p>
                      This account is currently marked as <strong>{accountStatusLabel || "inactive"}</strong> and can no
                      longer access the portal.
                    </p>
                    <p>If you think this is a mistake, contact an administrator.</p>
                    <button onClick={() => auth.signOut()}>Logout</button>
                  </div>
                </PageTitle>
              }
            />
          </Routes>
        </Router>
      </>
    );
  }

  return (
    <>
      {/* ✅ ADDED THIS COMPONENT HERE */}
      <VersionChecker />

      <Router>
        <Routes>
          {/* =========================
              PUBLIC ROUTES (Not logged in)
             ========================= */}
          {!user && (
            <>
              <Route
                path="/"
                element={
                  <PageTitle title="True Education Cumming">
                    <Homepage />
                  </PageTitle>
                }
              />
              <Route
                path="/login"
                element={
                  <PageTitle title="Login | True Education Cumming">
                    <Login />
                  </PageTitle>
                }
              />
              <Route
                path="/signup"
                element={
                  <PageTitle title="Get Started | True Education Cumming">
                    <Signup />
                  </PageTitle>
                }
              />

              {/* Tutoring pages (PUBLIC) */}
              <Route
                path="/tutoring"
                element={
                  <PageTitle title="Tutoring | True Education Cumming">
                    <TutoringMain />
                  </PageTitle>
                }
              />
              <Route
                path="/tutoring/sat"
                element={
                  <PageTitle title="SAT Tutoring | True Education Cumming">
                    <SATTutoring />
                  </PageTitle>
                }
              />
              <Route
                path="/tutoring/ap"
                element={
                  <PageTitle title="AP Tutoring | True Education Cumming">
                    <APTutoring />
                  </PageTitle>
                }
              />
              <Route
                path="/admissions"
                element={
                  <PageTitle title="Admissions | True Education Cumming">
                    <AdmissionsPage />
                  </PageTitle>
                }
              />
              <Route
                path="/about"
                element={
                  <PageTitle title="About | True Education Cumming">
                    <AboutPage />
                  </PageTitle>
                }
              />
              <Route
                path="/contact"
                element={
                  <PageTitle title="Contact | True Education Cumming">
                    <ContactPage />
                  </PageTitle>
                }
              />

              {/* Redirect random URLs to Homepage */}
              <Route path="*" element={<Navigate to="/" replace />} />
            </>
          )}

          {/* =========================
              PROTECTED ROUTES (Logged in)
             ========================= */}
          {user && (
            <>
              {/* Allow logged-in users to still visit public marketing pages */}
              <Route
                path="/tutoring"
                element={
                  <PageTitle title="Tutoring | True Education Cumming">
                    <TutoringMain />
                  </PageTitle>
                }
              />
              <Route
                path="/tutoring/sat"
                element={
                  <PageTitle title="SAT Tutoring | True Education Cumming">
                    <SATTutoring />
                  </PageTitle>
                }
              />
              <Route
                path="/tutoring/ap"
                element={
                  <PageTitle title="AP Tutoring | True Education Cumming">
                    <APTutoring />
                  </PageTitle>
                }
              />
              <Route
                path="/admissions"
                element={
                  <PageTitle title="Admissions | True Education Cumming">
                    <AdmissionsPage />
                  </PageTitle>
                }
              />
              <Route
                path="/about"
                element={
                  <PageTitle title="About | True Education Cumming">
                    <AboutPage />
                  </PageTitle>
                }
              />
              <Route
                path="/contact"
                element={
                  <PageTitle title="Contact | True Education Cumming">
                    <ContactPage />
                  </PageTitle>
                }
              />

              <Route
                path="/session-detail"
                element={
                  <PageTitle title="Session Detail | True Education Cumming">
                    <SessionLogDetailPage />
                  </PageTitle>
                }
              />
              <Route
                path="/progress-report/:reportId"
                element={
                  <PageTitle title="Progress Report | True Education Cumming">
                    <ProgressReportDetailPage />
                  </PageTitle>
                }
              />
              <Route
                path="/tutor-session-form"
                element={
                  <PageTitle title="Session Log | True Education Cumming">
                    <TutorSessionLogModal />
                  </PageTitle>
                }
              />

              {/* Deep-link route for push notification clicks.
                  URL format: /announcement?id=<announcementId>
                  (query parameter, not path parameter — consistent with other forms).
                  Placed in the shared authenticated block so any logged-in role
                  (tutor, admin) can follow a notification link.
                  AnnouncementDetailPage reads `id` via useSearchParams() and
                  fetches its own data directly from Firestore — no list dependency. */}
              <Route
                path="/announcement"
                element={
                  <PageTitle title="Announcement | True Education Cumming">
                    <AnnouncementDetailPage />
                  </PageTitle>
                }
              />

              {userRole === "admin" && (
                <>
                  <Route
                    path="/"
                    element={
                      <PageTitle title="Admin Dashboard | True Education Cumming">
                        <AdminDashboard />
                      </PageTitle>
                    }
                  />
                  {/* Standalone, focused report tab (no admin sidebar/chrome).
                      More specific than the "/diagnostics/*" dashboard route below,
                      so it wins. Opened in a new tab from Diagnostic Reports. */}
                  <Route
                    path="/diagnostics/report/:type/:variant"
                    element={
                      <PageTitle title="Diagnostic Report | True Education Cumming">
                        <StandaloneDiagnosticReport />
                      </PageTitle>
                    }
                  />
                  {/* Standalone SAT Plan workspace (own tab/URL). More specific
                      than "/diagnostics/*" below, so it wins. */}
                  <Route
                    path="/diagnostics/plan"
                    element={
                      <PageTitle title="SAT Plan | True Education Cumming">
                        <SatPlanWorkspacePage />
                      </PageTitle>
                    }
                  />
                  {/* Standalone custom-plan authoring page (own tab/URL). Any
                      subject, not limited to SAT/ACT. More specific than
                      "/students/*" below, so it wins. */}
                  <Route
                    path="/students/plan/custom"
                    element={
                      <PageTitle title="Custom Plan | True Education Cumming">
                        <CustomPlanWorkspacePage />
                      </PageTitle>
                    }
                  />
                  {ADMIN_PORTAL_ROUTE_PATHS.map((path) => (
                    <Route
                      key={path}
                      path={path}
                      element={
                        <PageTitle title="Admin Dashboard | True Education Cumming">
                          <AdminDashboard />
                        </PageTitle>
                      }
                    />
                  ))}
                </>
              )}

              {userRole === "student" && (
                <Route
                  path="/"
                  element={
                    <PageTitle title="Student Dashboard | True Education Cumming">
                      <StudentDashboard />
                    </PageTitle>
                  }
                />
              )}

              {userRole === "tutor" && (
                <>
                  <Route
                    path="/"
                    element={
                      <PageTitle title="Tutor Dashboard | True Education Cumming">
                        <TutorDashboard />
                      </PageTitle>
                    }
                  />
                  <Route
                    path="/tutor/resources/:studentId/:subject/:unit/:subcategory"
                    element={
                      <PageTitle title="Resources | True Education Cumming">
                        <SubcategoryResourceManager />
                      </PageTitle>
                    }
                  />
                  {/* Standalone, view-only deep-link targets opened in a new tab
                      from the unified Diagnostic Reports page. */}
                  <Route
                    path="/tutor/diagnostics/report/:type/:variant"
                    element={
                      <PageTitle title="Diagnostic Report | True Education Cumming">
                        <TutorDiagnosticReportPage />
                      </PageTitle>
                    }
                  />
                  {/* Custom-plan authoring page — teachers may create and edit
                      plans for any subject. More specific than "/students/*". */}
                  <Route
                    path="/students/plan/custom"
                    element={
                      <PageTitle title="Custom Plan | True Education Cumming">
                        <CustomPlanWorkspacePage />
                      </PageTitle>
                    }
                  />
                  {/* Clean URLs for each Tutor Portal sidebar page / subpage so
                      tutors get stable, shareable links and reliable browser
                      back/forward navigation. All render the same dashboard,
                      which picks the active tab from the URL. */}
                  {TUTOR_PORTAL_ROUTE_PATHS.map((path) => (
                    <Route
                      key={path}
                      path={path}
                      element={
                        <PageTitle title="Tutor Dashboard | True Education Cumming">
                          <TutorDashboard />
                        </PageTitle>
                      }
                    />
                  ))}
                </>
              )}

              {userRole === "parent" && (
                <Route
                  path="/"
                  element={
                    <PageTitle title="Parent Dashboard | True Education Cumming">
                      <PlaceholderDashboard role="Parent" />
                    </PageTitle>
                  }
                />
              )}

              {!userRole && (
                <Route
                  path="/"
                  element={
                    <PageTitle title="Error | True Education Cumming">
                      <div style={{padding: 20}}>
                        <h3>Access Issue</h3>
                        <p>We see you are logged in, but we couldn't find your role.</p>
                        <p>Technical details: Neither custom claims nor Firestore 'users' collection contained a role.</p>
                        <button onClick={() => auth.signOut()}>Logout</button>
                      </div>
                    </PageTitle>
                  }
                />
              )}

              {/* If a logged-in user tries to go to /login, send them to their dashboard */}
              <Route path="/login" element={<Navigate to="/" replace />} />

              {/* Fallback for logged-in users */}
              <Route path="*" element={<Navigate to="/" replace />} />
            </>
          )}
        </Routes>
      </Router>
    </>
  );
}

export default App;
